import React, { useState } from 'react';
import { renderContent } from '../renderContent';
import { apiClient } from '../../utils/apiClient';
import Modal from '../common/Modal';
import { AlertTriangle, Zap, Search, PartyPopper, XCircle, Trophy, Flag, BarChart2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function PlacementTest({ chapterId, levels, user, onClose, onSuccess, initialLevelIndex = 0 }) {
    const [currentLevelIndex, setCurrentLevelIndex] = useState(initialLevelIndex);
    const [gameState, setGameState] = useState('intro'); // 'intro', 'loading', 'active', 'level_completed', 'summary'
    const [quizzes, setQuizzes] = useState([]);
    const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
    const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
    const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
    const [score, setScore] = useState(0);
    const [isSubmittingLevel, setIsSubmittingLevel] = useState(false);
    const [unlockedLevels, setUnlockedLevels] = useState([]);
    const [errorMsg, setErrorMsg] = useState('');

    const currentLevel = levels[currentLevelIndex];
    const levelLabel = currentLevel ? `Niv. ${currentLevel.level_number}` : '';

    // Load quizzes for current level
    const startTestingLevel = async (index) => {
        setGameState('loading');
        setErrorMsg('');
        const lvl = levels[index];
        try {
            const res = await apiClient(`${API_BASE}/placement_test.php?action=get_questions&chapter_id=${chapterId}&level_number=${lvl.level_number}`);
            const data = await res.json();
            if (data.success && data.quizzes && data.quizzes.length > 0) {
                // Shuffle option lists for each quiz
                const preparedQuizzes = data.quizzes.map(q => {
                    const quizData = q.answer || {};
                    let options = quizData.options || [];
                    options = [...options];
                    // Shuffle
                    for (let i = options.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [options[i], options[j]] = [options[j], options[i]];
                    }
                    return { ...q, ...quizData, options };
                });
                setQuizzes(preparedQuizzes);
                setCurrentQuizIndex(0);
                setSelectedOptionIndex(null);
                setIsAnswerSubmitted(false);
                setScore(0);
                setGameState('active');
            } else {
                setErrorMsg(data.message || "Impossible de récupérer les questions pour ce niveau.");
                setGameState('intro');
            }
        } catch (err) {
            console.error("Error loading placement quizzes:", err);
            setErrorMsg("Une erreur est survenue lors de la récupération des questions.");
            setGameState('intro');
        }
    };

    const handleOptionSelect = (index) => {
        if (isAnswerSubmitted) return;
        setSelectedOptionIndex(index);
    };

    const handleAnswerSubmit = () => {
        if (selectedOptionIndex === null || isAnswerSubmitted) return;
        setIsAnswerSubmitted(true);

        const currentQuiz = quizzes[currentQuizIndex];
        const isCorrect = currentQuiz.options[selectedOptionIndex]?.isCorrect;
        if (isCorrect) {
            setScore(prev => prev + 1);
        }
    };

    const handleNextQuestion = () => {
        if (currentQuizIndex + 1 < quizzes.length) {
            setCurrentQuizIndex(prev => prev + 1);
            setSelectedOptionIndex(null);
            setIsAnswerSubmitted(false);
        } else {
            completeLevel();
        }
    };

    const completeLevel = async () => {
        setGameState('loading');
        setIsSubmittingLevel(true);
        const finalScore = score;

        try {
            // Call backend to submit test results and record attempt
            const res = await apiClient(`${API_BASE}/placement_test.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'submit_test',
                    chapter_id: chapterId,
                    level_number: currentLevel.level_number,
                    score: finalScore
                })
            });
            const data = await res.json();
            if (data.success) {
                if (data.passed) {
                    setUnlockedLevels(prev => [...prev, currentLevel.level_number]);
                    setGameState('level_completed');
                } else {
                    // Log attempt but did not pass
                    setGameState('summary');
                }
            } else {
                setErrorMsg(data.message || "Erreur lors de l'enregistrement de vos résultats.");
                setGameState('intro');
            }
        } catch (err) {
            console.error("Error submitting level pass:", err);
            setErrorMsg("Erreur de connexion lors de la validation du niveau.");
            setGameState('intro');
        } finally {
            setIsSubmittingLevel(false);
        }
    };

    const handleNextLevel = () => {
        if (currentLevelIndex + 1 < levels.length) {
            const nextIdx = currentLevelIndex + 1;
            setCurrentLevelIndex(nextIdx);
            startTestingLevel(nextIdx);
        } else {
            setGameState('summary');
        }
    };

    const handleFinished = () => {
        if (onSuccess) onSuccess();
        onClose();
    };

    const activeQuiz = quizzes[currentQuizIndex];

    return (
        <Modal
            isOpen={true}
            onClose={gameState !== 'loading' && gameState !== 'active' ? onClose : null}
            title="Test de Positionnement IA"
            subtitle={`Évaluation intelligente — ${levelLabel}`}
            size="lg"
            preventCloseOnOverlay={true}
            hideCloseButton={gameState === 'loading' || gameState === 'active'}
        >
            {errorMsg && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 rounded-2xl text-xs font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-red-500" />
                    <span>{errorMsg}</span>
                </div>
            )}

            {/* INTRO STATE */}
            {gameState === 'intro' && (
                <div className="space-y-6 text-center py-4">
                    <div className="w-16 h-16 bg-blue-500/10 rounded-3xl flex items-center justify-center mx-auto shadow-inner">
                        <Zap className="w-8 h-8 text-blue-500 fill-blue-500/20" />
                    </div>
                    <div className="space-y-2">
                        <h4 className="text-xl font-extrabold text-gray-800 dark:text-white">Évalue ton niveau réel !</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
                            Ce test adaptatif évalue ton niveau dans ce chapitre. Si tu obtiens <strong className="text-blue-500">80% ou plus</strong> de bonnes réponses à un niveau, tu le valideras directement et passeras au niveau supérieur !
                        </p>
                    </div>
                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-gray-100 dark:border-white/[0.04] text-xs text-left max-w-md mx-auto space-y-3">
                        <h5 className="font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <Search className="w-4 h-4 text-blue-500" />
                            <span>Comment fonctionne le test ?</span>
                        </h5>
                        <ul className="space-y-2 text-gray-600 dark:text-gray-400 list-disc list-inside">
                            <li>Le test porte sur le <strong>{levelLabel}</strong>.</li>
                            <li>Il contient exactement <strong>10 questions</strong> à choix unique.</li>
                            <li>Tu dois obtenir au moins <strong>8 bonnes réponses</strong> pour valider ce niveau.</li>
                            <li><strong>Important :</strong> Tu as droit à <strong>1 essai par jour et par niveau</strong>. Réfléchis bien avant de répondre !</li>
                        </ul>
                    </div>
                    <button
                        onClick={() => startTestingLevel(currentLevelIndex)}
                        className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold transition-all shadow-lg shadow-blue-500/20 hover:scale-[1.02] cursor-pointer"
                    >
                        Commencer le test du {levelLabel}
                    </button>
                </div>
            )}

            {/* LOADING STATE */}
            {gameState === 'loading' && (
                <div className="flex flex-col items-center justify-center py-16 space-y-4">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-semibold animate-pulse text-center">
                        {isSubmittingLevel ? "Validation et enregistrement de tes résultats..." : "Génération et chargement des questions personnalisées..."}
                    </p>
                </div>
            )}

            {/* ACTIVE TEST STATE */}
            {gameState === 'active' && activeQuiz && (
                <div className="space-y-6">
                    {/* Quiz Progress header */}
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider pb-2 border-b border-gray-100 dark:border-white/[0.04]">
                        <span>Évaluation : {levelLabel}</span>
                        <span>Question {currentQuizIndex + 1} / {quizzes.length}</span>
                    </div>

                    {/* Question text */}
                    <div className="text-lg font-bold text-gray-800 dark:text-white leading-relaxed bg-blue-500/5 dark:bg-white/[0.01] p-6 rounded-2xl border border-blue-500/10 dark:border-white/[0.04] shadow-inner">
                        {renderContent(activeQuiz.question)}
                    </div>

                    {/* Option list */}
                    <div className="grid grid-cols-1 gap-3">
                        {activeQuiz.options.map((option, idx) => {
                            const isSelected = selectedOptionIndex === idx;
                            let btnStyle = "border-gray-200 dark:border-white/[0.06] hover:border-blue-400/50 hover:bg-blue-500/5 dark:hover:bg-white/[0.02]";
                            
                            if (isAnswerSubmitted) {
                                if (option.isCorrect) {
                                    btnStyle = "border-emerald-500 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.1)]";
                                } else if (isSelected) {
                                    btnStyle = "border-red-500 bg-red-500/10 text-red-700 dark:text-red-400";
                                } else {
                                    btnStyle = "opacity-50 border-gray-100 dark:border-white/[0.02] cursor-not-allowed";
                                }
                            } else if (isSelected) {
                                btnStyle = "border-blue-500 bg-blue-500/10 text-blue-700 dark:text-blue-400 ring-2 ring-blue-500/20";
                            }

                            return (
                                <button
                                    key={idx}
                                    disabled={isAnswerSubmitted}
                                    onClick={() => handleOptionSelect(idx)}
                                    className={`p-4 rounded-xl border text-left text-sm font-semibold transition-all duration-300 flex items-start gap-3 cursor-pointer ${btnStyle}`}
                                >
                                    <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-xs font-bold transition-all ${
                                        isAnswerSubmitted && option.isCorrect ? 'bg-emerald-500 border-emerald-500 text-white' :
                                        isAnswerSubmitted && isSelected ? 'bg-red-500 border-red-500 text-white' :
                                        isSelected ? 'bg-blue-500 border-blue-500 text-white' :
                                        'bg-slate-50 dark:bg-white/[0.04] border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-gray-400'
                                    }`}>
                                        {String.fromCharCode(65 + idx)}
                                    </span>
                                    <span className="flex-1 leading-snug">{renderContent(option.text)}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Detailed feedback after submit */}
                    {isAnswerSubmitted && (
                        <div className="p-5 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-gray-100 dark:border-white/[0.04] text-xs space-y-2 animate-fade-in">
                            <p className="font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                                {activeQuiz.options[selectedOptionIndex]?.isCorrect ? (
                                    <>
                                        <PartyPopper className="w-5 h-5 text-emerald-500" />
                                        <span>Excellent travail ! C'est correct.</span>
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="w-5 h-5 text-red-500" />
                                        <span>Ce n'est pas tout à fait ça.</span>
                                    </>
                                )}
                            </p>
                            {activeQuiz.options[selectedOptionIndex]?.feedback && (
                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                                    {renderContent(activeQuiz.options[selectedOptionIndex].feedback)}
                                </p>
                            )}
                            {activeQuiz.feedback && (
                                <p className="text-gray-500 dark:text-gray-500 pt-1 border-t border-gray-100 dark:border-white/[0.04] leading-relaxed">
                                    <strong>Correction générale :</strong> {renderContent(activeQuiz.feedback)}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Active Action button */}
                    <div className="flex justify-end pt-2">
                        {!isAnswerSubmitted ? (
                            <button
                                disabled={selectedOptionIndex === null}
                                onClick={handleAnswerSubmit}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-white/[0.04] disabled:text-gray-500 text-white font-bold rounded-xl text-xs transition-all shadow-md shadow-blue-500/10 cursor-pointer"
                            >
                                Valider ma réponse
                            </button>
                        ) : (
                            <button
                                onClick={handleNextQuestion}
                                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition-all shadow-md shadow-emerald-500/10 cursor-pointer"
                            >
                                {currentQuizIndex + 1 < quizzes.length ? "Question suivante →" : "Terminer le niveau"}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* LEVEL COMPLETED SUCCESS STATE */}
            {gameState === 'level_completed' && (
                <div className="space-y-6 text-center py-6 animate-fade-in">
                    <div className="w-20 h-20 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center justify-center mx-auto shadow-lg animate-bounce">
                        <Trophy className="w-10 h-10 text-emerald-500 fill-emerald-500/20" />
                    </div>
                    <div className="space-y-2">
                        <h4 className="text-2xl font-extrabold text-gray-800 dark:text-white">Félicitations !</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Tu as validé le <strong className="text-emerald-500">{levelLabel}</strong> avec un score de <strong className="text-emerald-500">{score} / {quizzes.length}</strong> ({(score / quizzes.length) * 100}%) !
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                            Toutes les notions, fiches et quiz de ce niveau ont été débloqués et marqués comme maîtrisés.
                        </p>
                    </div>
                    <div className="flex justify-center gap-4 pt-4">
                        {currentLevelIndex + 1 < levels.length ? (
                            <button
                                onClick={handleNextLevel}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs transition-all shadow-lg shadow-blue-500/15 cursor-pointer"
                            >
                                Passer le test du Niv. {levels[currentLevelIndex + 1]?.level_number} →
                            </button>
                        ) : (
                            <button
                                onClick={() => setGameState('summary')}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs transition-all shadow-lg shadow-blue-500/15 cursor-pointer"
                            >
                                Voir le récapitulatif
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* SUMMARY / FAIL END STATE */}
            {gameState === 'summary' && (
                <div className="space-y-6 text-center py-6 animate-fade-in">
                    <div className="w-20 h-20 bg-blue-500/10 border border-blue-500/20 rounded-3xl flex items-center justify-center mx-auto shadow-lg">
                        <Flag className="w-10 h-10 text-blue-500 fill-blue-500/20" />
                    </div>
                    <div className="space-y-2">
                        <h4 className="text-2xl font-extrabold text-gray-800 dark:text-white">Bilan du Test</h4>
                        {unlockedLevels.length > 0 ? (
                            <div className="space-y-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                    Excellent ! Grâce à tes connaissances, tu as validé avec succès {unlockedLevels.length} niveau{unlockedLevels.length > 1 ? 's' : ''} :
                                </p>
                                <div className="flex flex-wrap justify-center gap-2 pt-2">
                                    {unlockedLevels.map((lvlNum) => {
                                        return (
                                            <span key={lvlNum} className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 font-bold rounded-full text-xs shadow-inner">
                                                ✓ Niv. {lvlNum}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
                                    Le test est terminé pour aujourd'hui ! Tu as obtenu un score de <strong className="text-blue-500">{score} / 10</strong>. Il te faut au moins 8 bonnes réponses pour débloquer automatiquement le niveau supérieur.
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 font-medium">
                                    N'hésite pas à réviser tes cours et t'exercer. Tu pourras retenter le test demain !
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="p-5 rounded-2xl bg-blue-500/5 dark:bg-white/[0.01] border border-blue-500/10 dark:border-white/[0.04] text-xs text-left max-w-md mx-auto space-y-2">
                        <p className="font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <BarChart2 className="w-4 h-4 text-blue-500" />
                            <span>Prochaines étapes :</span>
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                            Tous tes nouveaux cours, fiches de révision et exercices débloqués sont maintenant disponibles sur ton tableau de bord.
                        </p>
                    </div>

                    <button
                        onClick={handleFinished}
                        className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl transition-all shadow-lg shadow-blue-500/15 cursor-pointer"
                    >
                        Accéder à mes cours débloqués
                    </button>
                </div>
            )}
        </Modal>
    );
}

export default PlacementTest;
